import { useCallback, useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  CircleSlash,
  FlaskConical,
  KeyRound,
  Plug,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { OpsShell } from "@/components/admin/OpsShell";
import { Btn, Card, Loading, T, fmtDate, usePoll } from "@/components/admin/ui";
import { api } from "@/lib/api";

const HEALTH_TONE = {
  healthy: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 ring-emerald-600/20",
  down: "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 ring-rose-600/20",
  unknown: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 ring-slate-500/20",
};

const HealthPill = ({ state, testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${HEALTH_TONE[state] || HEALTH_TONE.unknown}`}
  >
    {state === "healthy" ? <Check className="w-3 h-3" /> : state === "down" ? <AlertTriangle className="w-3 h-3" /> : <CircleSlash className="w-3 h-3" />}
    {state}
  </span>
);

const Stat = ({ label, value }) => (
  <div>
    <p className={T.label}>{label}</p>
    <p className={`text-sm mt-0.5 ${T.num} text-slate-800 dark:text-slate-200`}>{value}</p>
  </div>
);

export default function AdminAI() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState("");
  const [results, setResults] = useState({});
  const [proposalResult, setProposalResult] = useState(null);
  const [addProvider, setAddProvider] = useState("");
  const [addModel, setAddModel] = useState("");

  const load = useCallback(async () => {
    const [p, e] = await Promise.all([api.get("/admin/ai/providers"), api.get("/admin/ai/events?limit=30")]);
    setData(p.data);
    setEvents(e.data.events || []);
    setDraft((prev) => prev ?? { chain: p.data.config.chain, disabled: p.data.config.disabled, emergent_fallback: p.data.config.emergent_fallback });
  }, []);

  usePoll(() => { load().catch(() => toast.error("Could not load AI providers")); }, 30000, []);

  if (!data || !draft) return <OpsShell title="AI providers"><Loading label="Loading providers" /></OpsShell>;

  const configurable = data.providers.filter((p) => !p.is_fallback);
  const fallback = data.providers.find((p) => p.is_fallback);
  const dirty = JSON.stringify(draft) !== JSON.stringify({ chain: data.config.chain, disabled: data.config.disabled, emergent_fallback: data.config.emergent_fallback });

  const move = (i, dir) => {
    const chain = [...draft.chain];
    const j = i + dir;
    if (j < 0 || j >= chain.length) return;
    [chain[i], chain[j]] = [chain[j], chain[i]];
    setDraft({ ...draft, chain });
  };

  const removeAt = (i) => setDraft({ ...draft, chain: draft.chain.filter((_, k) => k !== i) });

  const addCandidate = () => {
    if (!addProvider || !addModel) return;
    if (draft.chain.some((c) => c.provider === addProvider && c.model === addModel)) {
      toast.error("That provider + model is already in the chain");
      return;
    }
    setDraft({ ...draft, chain: [...draft.chain, { provider: addProvider, model: addModel }] });
    setAddModel("");
  };

  const toggleDisabled = (pid) => {
    const disabled = draft.disabled.includes(pid) ? draft.disabled.filter((p) => p !== pid) : [...draft.disabled, pid];
    setDraft({ ...draft, disabled });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/ai/config", draft);
      setData({ ...data, config: r.data.config, resolved_chain: r.data.resolved_chain });
      setDraft({ chain: r.data.config.chain, disabled: r.data.config.disabled, emergent_fallback: r.data.config.emergent_fallback });
      toast.success("AI routing saved");
      load().catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save routing");
    } finally {
      setSaving(false);
    }
  };

  const testProvider = async (pid, model) => {
    setTesting(`${pid}:${model}`);
    try {
      const r = await api.post(`/admin/ai/providers/${pid}/test?model=${encodeURIComponent(model)}`);
      setResults({ ...results, [`${pid}:${model}`]: r.data });
      r.data.ok ? toast.success(`${pid} responded in ${r.data.ms}ms`) : toast.error(`${pid} failed: ${r.data.error_kind}`);
      load().catch(() => {});
    } catch (e) {
      const detail = e?.response?.data?.detail || "Test failed";
      setResults({ ...results, [`${pid}:${model}`]: { ok: false, error: detail } });
      toast.error(detail);
    } finally {
      setTesting("");
    }
  };

  const runTestProposal = async () => {
    setTesting("proposal");
    setProposalResult(null);
    try {
      const r = await api.post("/admin/ai/test-proposal");
      setProposalResult(r.data);
      toast.success(`Generated ${r.data.blocks} blocks in ${(r.data.ms / 1000).toFixed(1)}s`);
      load().catch(() => {});
    } catch (e) {
      const d = e?.response?.data?.detail;
      setProposalResult({ ok: false, error: typeof d === "string" ? d : d?.message || "Generation failed", attempts: d?.attempts });
      toast.error("Test generation failed");
    } finally {
      setTesting("");
    }
  };

  const providerLabel = (pid) => data.providers.find((p) => p.id === pid)?.label || pid;
  const addModels = data.providers.find((p) => p.id === addProvider)?.models || [];

  return (
    <OpsShell
      title="AI providers"
      subtitle="Routing chain, health and usage for every proposal generation call"
      actions={
        <Btn variant="primary" data-testid="ai-test-proposal-btn" disabled={testing === "proposal"} onClick={runTestProposal}>
          <Sparkles className="w-3.5 h-3.5" /> {testing === "proposal" ? "Generating…" : "Generate test proposal"}
        </Btn>
      }
    >
      {/* -------------------------------- providers ------------------------------- */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="ai-provider-grid">
        {configurable.map((p) => {
          const off = draft.disabled.includes(p.id);
          return (
            <div key={p.id} className={`${T.card} p-4 space-y-3`} data-testid={`ai-provider-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-slate-900 dark:text-slate-50">{p.label}</p>
                  <p className={`text-[11px] mt-0.5 ${T.muted} flex items-center gap-1`}>
                    <KeyRound className="w-3 h-3" />
                    {p.key_present ? `${p.key_env} configured` : `${p.key_env} missing`}
                  </p>
                </div>
                <HealthPill state={p.health.state} testid={`ai-health-${p.id}`} />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Stat label="Calls 7d" value={p.metrics.calls} />
                <Stat label="Failed" value={p.metrics.failed} />
                <Stat label="Avg" value={`${p.metrics.avg_ms}ms`} />
              </div>

              <div className="space-y-1.5">
                {p.models.map((m) => {
                  const k = `${p.id}:${m.id}`;
                  const res = results[k];
                  return (
                    <div key={m.id} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-1.5">
                      <div className="min-w-0">
                        <p className="text-[12px] font-medium text-slate-700 dark:text-slate-300 truncate">{m.id}</p>
                        {res && (
                          <p className={`text-[11px] ${res.ok ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`} data-testid={`ai-test-result-${p.id}`}>
                            {res.ok ? `ok · ${res.ms}ms` : `${res.error_kind || "error"}`}
                          </p>
                        )}
                      </div>
                      <Btn
                        data-testid={`ai-test-btn-${p.id}-${m.id.replace(/[^a-z0-9]/gi, "")}`}
                        disabled={!p.key_present || testing === k}
                        onClick={() => testProvider(p.id, m.id)}
                      >
                        <Plug className="w-3 h-3" /> {testing === k ? "…" : "Test"}
                      </Btn>
                    </div>
                  );
                })}
              </div>

              <button
                data-testid={`ai-toggle-${p.id}`}
                onClick={() => toggleDisabled(p.id)}
                className={`${T.btn} w-full ${off ? T.btnGhost : T.btnDanger}`}
              >
                {off ? <><Check className="w-3.5 h-3.5" /> Enable provider</> : <><X className="w-3.5 h-3.5" /> Disable provider</>}
              </button>
            </div>
          );
        })}
      </div>

      {/* --------------------------------- chain ---------------------------------- */}
      <Card
        title="Routing chain"
        hint="Every proposal generation walks this order. Transient failures retry once, then fail over to the next entry."
        testid="ai-chain-card"
        pad
        actions={
          <Btn variant="primary" data-testid="ai-save-config-btn" disabled={!dirty || saving} onClick={save}>
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </Btn>
        }
      >
        <ol className="space-y-2" data-testid="ai-chain-list">
          {draft.chain.map((c, i) => {
            const off = draft.disabled.includes(c.provider);
            const missing = !data.providers.find((p) => p.id === c.provider)?.key_present;
            return (
              <li
                key={`${c.provider}-${c.model}`}
                data-testid={`ai-chain-row-${i}`}
                className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2"
              >
                <span className={`w-16 shrink-0 text-[10px] font-semibold uppercase tracking-wider ${i === 0 ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400"}`}>
                  {i === 0 ? "Primary" : `Backup ${i}`}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200 truncate">
                    {providerLabel(c.provider)} <span className={`${T.muted} font-normal`}>· {c.model}</span>
                  </p>
                  {(off || missing) && (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                      {missing ? "No API key — skipped at runtime" : "Provider disabled — skipped at runtime"}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Btn data-testid={`ai-chain-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="px-2"><ArrowUp className="w-3.5 h-3.5" /></Btn>
                  <Btn data-testid={`ai-chain-down-${i}`} disabled={i === draft.chain.length - 1} onClick={() => move(i, 1)} className="px-2"><ArrowDown className="w-3.5 h-3.5" /></Btn>
                  <Btn variant="danger" data-testid={`ai-chain-remove-${i}`} disabled={draft.chain.length <= 1} onClick={() => removeAt(i)} className="px-2"><Trash2 className="w-3.5 h-3.5" /></Btn>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <select
            data-testid="ai-add-provider"
            className={`${T.input} text-[13px]`}
            value={addProvider}
            onChange={(e) => { setAddProvider(e.target.value); setAddModel(""); }}
          >
            <option value="">Add provider…</option>
            {configurable.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <select data-testid="ai-add-model" className={`${T.input} text-[13px]`} value={addModel} onChange={(e) => setAddModel(e.target.value)} disabled={!addProvider}>
            <option value="">Model…</option>
            {addModels.map((m) => <option key={m.id} value={m.id}>{m.id}</option>)}
          </select>
          <Btn data-testid="ai-add-candidate-btn" disabled={!addProvider || !addModel} onClick={addCandidate}><Plus className="w-3.5 h-3.5" /> Add to chain</Btn>
        </div>

        <label className="mt-4 flex items-start gap-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="ai-emergent-fallback"
            className="mt-0.5 accent-emerald-600"
            checked={draft.emergent_fallback}
            onChange={(e) => setDraft({ ...draft, emergent_fallback: e.target.checked })}
          />
          <span>
            <span className="text-[13px] font-medium text-slate-800 dark:text-slate-200">Keep the Emergent Universal Key as a last-resort fallback</span>
            <span className={`block text-[11px] ${T.muted}`}>
              {fallback?.label} · {fallback?.models?.[0]?.id} — used only when every provider above fails.
            </span>
          </span>
        </label>

        <p className={`mt-3 text-[11px] ${T.muted}`} data-testid="ai-resolved-chain">
          Live order: {data.resolved_chain.map((c) => `${providerLabel(c.provider)}/${c.model}`).join(" → ") || "none"}
          {data.config.updated_at ? ` · last changed ${fmtDate(data.config.updated_at)} by ${data.config.updated_by}` : ""}
        </p>
      </Card>

      {/* ----------------------------- test proposal ----------------------------- */}
      {proposalResult && (
        <Card title="Test generation" hint="Runs the live chain end to end. Nothing is saved as a proposal." testid="ai-test-proposal-result" pad>
          {proposalResult.ok ? (
            <div className="space-y-2">
              <p className="text-[13px] text-slate-800 dark:text-slate-200">
                <FlaskConical className="inline w-3.5 h-3.5 mr-1 text-emerald-600" />
                <strong>{proposalResult.title}</strong> — {proposalResult.blocks} blocks in {(proposalResult.ms / 1000).toFixed(1)}s, served by{" "}
                <span className={T.num}>{proposalResult.served_by?.provider}/{proposalResult.served_by?.model}</span>
              </p>
              <p className={`text-[11px] ${T.muted}`}>{proposalResult.block_types?.join(" · ")}</p>
              <p className={`text-[11px] ${T.muted}`}>{proposalResult.unverified} unverified-claim flag(s)</p>
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-[13px] text-rose-600 dark:text-rose-400">{proposalResult.error}</p>
              {proposalResult.attempts?.map((a, i) => (
                <p key={`${a.provider}-${i}`} className={`text-[11px] ${T.num} ${T.muted}`}>{a.provider}/{a.model} → {a.error_kind} ({a.ms}ms)</p>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* -------------------------------- events --------------------------------- */}
      <Card title="Recent AI calls" hint="Prompts are never stored — only size, latency and outcome" testid="ai-events-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                {["When", "Provider", "Model", "Purpose", "Status", "Latency", "Prompt", "Error"].map((h) => (
                  <th key={h} className={T.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="ai-events-body">
              {events.map((e, i) => (
                <tr key={`${e.at}-${i}`} className={T.row}>
                  <td className={`${T.td} whitespace-nowrap`}>{fmtDate(e.at)}</td>
                  <td className={T.td}>{providerLabel(e.provider)}</td>
                  <td className={`${T.td} ${T.num}`}>{e.model}</td>
                  <td className={T.td}>{e.purpose}</td>
                  <td className={T.td}>
                    <span className={e.status === "ok" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}>{e.status}</span>
                  </td>
                  <td className={`${T.td} ${T.num}`}>{e.ms}ms</td>
                  <td className={`${T.td} ${T.num}`}>{e.prompt_chars} ch</td>
                  <td className={`${T.td} max-w-[280px] truncate`}>{e.error_kind || "—"}</td>
                </tr>
              ))}
              {!events.length && (
                <tr><td className={T.td} colSpan={8}><span className={T.muted}>No AI calls recorded yet.</span></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </OpsShell>
  );
}
